import { useTranslation } from "react-i18next";
import { Box, Divider, Skeleton } from "@chakra-ui/react";
import {
  BooleanFilter,
  CreatedByFilter,
  NoDataPlaceholder,
  QuickActions,
  Table,
  TablePageSkeleton,
} from "components/ui";
import { RiAddLine, RiEye2Fill } from "react-icons/ri";
import getColumns from "helpers/getColumns";
import {
  PolicyAssetNode,
  useGetAllPolicyAssetsByPolicyTypeQuery,
} from "graphql/queries/generated/queries";
import { createdByFilterFunc } from "components/ui/CreatedByFilter";
import { useNavigate } from "react-router-dom";
import parseAmount from "helpers/parseAmount";
import { SPIN_ROUTES } from "routes/constants";
// import placeholderPNG from "assets/placeholder.png";

const CUSTOM_FIELDS = [
  "asset",
  "asset.title",
  "asset.author",
  "asset.creationPeriod",
  "coveredRiskType",
  "evaluationType",
  "coverType",
  "image",
  "lenderAddress",
  "deliveryAddress",
  "pickupAddress",
  "policy",
  "policy.offerCode",
  "policy.policyCode",
  "insuredValue",
  "asset.validated",
  "asset.createdBy",
  "asset.actualDateAndPeriod",
  "asset.mainImage",
];
const CUSTOM_COLUMNS = [
  {
    Header: "Category",
    Footer: "",
    accessor: "asset.category",
    Cell: ({ cell: { value } }: { cell: { value: string } }) => value,
    disableFilters: true,
    weight: 101,
  },
  {
    Header: "Title",
    Footer: "",
    accessor: "asset.title",
    Cell: ({ cell: { value } }: { cell: { value: string } }) => value,
    disableFilters: true,
    weight: 99,
  },
  {
    Header: "Image",
    Footer: "",
    accessor: "asset.mainImage",
    Cell: ({ cell: { value } }: { cell: { value: string } }) =>
      value ? <img src={value} alt="" /> : "",
    disableFilters: true,
    weight: 102,
  },
  {
    Header: "Author",
    Footer: "",
    accessor: "asset.author",
    Cell: ({ cell: { value } }: { cell: { value: string } }) => value,
    disableFilters: true,
    weight: 100,
  },
  {
    Header: "Creation Period",
    Footer: "",
    accessor: "asset.actualDateAndPeriod",
    Cell: ({ cell: { value } }: { cell: { value: string } }) => value,
    disableFilters: true,
    weight: 98,
  },
  {
    Header: "Lender",
    Footer: "",
    accessor: "lender.fullName",
    Cell: ({ cell: { value } }: { cell: { value: string } }) => value,
    disableFilters: true,
    weight: 97,
  },
  {
    Header: "Country",
    Footer: "",
    accessor: "",
    Cell: ({ cell: { value } }: { cell: { value: string } }) => <div />,
    disableFilters: true,
    weight: 97,
  },
  {
    Header: "Sum insured",
    Footer: "Sum insured",
    disableFilters: true,
    accessor: "insuredValue.amount",
    weight: 96,
    Cell: ({
      cell: {
        row: { original },
      },
    }: {
      cell: { row: { original: PolicyAssetNode } };
    }) => {
      const { insuredValue } = original;
      return parseAmount(insuredValue);
    },
  },

  {
    Header: "Dimensions",
    Footer: "",
    accessor: "asset.genericDimensions",
    Cell: ({ cell: { value } }: { cell: { value: string } }) => value,
    disableFilters: true,
    weight: 95,
  },
  {
    Header: "Technique",
    Footer: "",
    accessor: "asset.technique",
    Cell: ({ cell: { value } }: { cell: { value: string } }) => value,
    disableFilters: true,
    weight: 95,
  },
  {
    Header: "Inventory #",
    Footer: "",
    accessor: "asset.inventoryNumber",
    Cell: ({ cell: { value } }: { cell: { value: string } }) => value,
    disableFilters: true,
    weight: 95,
  },
  {
    Header: "",
    Footer: "",
    accessor: "asset.validated",
    Cell: ({ cell: { value } }: { cell: { value: string } }) => <span />,
    Filter: BooleanFilter,
    weight: 0,
  },
  {
    Header: "Created By",
    Footer: "Created By",
    accessor: "asset.createdBy.name",
    Cell: ({ cell: { value } }: { cell: { value: string } }) => value,
    Filter: CreatedByFilter,
    filter: createdByFilterFunc,
    weight: 0,
  },
];

const QUICK_ACTIONS = [
  {
    name: "Add new TE policy",
    href: SPIN_ROUTES.POLICY_TE_NEW,
    icon: <RiAddLine />,
  },
];

const TePoliciesObjectsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, loading, error, refetch } = useGetAllPolicyAssetsByPolicyTypeQuery({
    variables: {
      policy_PolicyType: "TEMPORARY_EXHIBITION",
    },
  });

  if (loading) return <TablePageSkeleton loading={loading} />;
  if (error) return <NoDataPlaceholder title={error.message} />;
  if (!data || !data.allPolicyAssets || data.allPolicyAssets.edges.length === 0)
    return (
      <Box>
        <QuickActions actions={QUICK_ACTIONS} />
        <Divider borderColor="gray.100" />
        <NoDataPlaceholder />
      </Box>
    );

  const {
    allPolicyAssets: {
      edges: [edge],
    },
  } = data;

  if (edge === null || !edge.node) return null;

  const dataToGetColumns = edge.node;
  const columns = getColumns({
    data: dataToGetColumns,
    customFields: CUSTOM_FIELDS,
    customColumns: CUSTOM_COLUMNS,
  });

  const onDelete = async (id: string) => {
    // await toggleIsActiveUser({
    //   variables: {
    //     input: {
    //       id,
    //     },
    //   },
    // });
    // await refetch();
  };

  return (
    <Box>
      {/* <QuickActions actions={QUICK_ACTIONS} /> */}
      <Divider borderColor="gray.100" />
      <Skeleton isLoaded={!loading}>
        <Table
          //   batchActions={BATCH_ACTIONS}
          columns={columns}
          hiddenColumns={[
            "lender",
            "lenderAddress",
            "delivery",
            "deliveryAddress",
            "pickupAddress",
            "shipper",
            "catNat",
            "exemption",
            "coverTerrorism",
            "objectLocationEntity",
          ]}
          data={data.allPolicyAssets.edges.flatMap((e) => e?.node)}
          deleteAction={(id: string) => onDelete(id)}
          refetch={refetch}
          customActions={[
            {
              icon: <RiEye2Fill />,
              name: "View policy",
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onClick: (row: { original: { policy: { id: string } } }) => {
                const {
                  original: { policy },
                } = row;
                navigate(`${SPIN_ROUTES.POLICY_TE}/${policy?.id}`);
              },
            },
          ]}
          //   deleteActionLoading={toggleIsActiveUserLoading}
        />
      </Skeleton>
    </Box>
  );
};

export default TePoliciesObjectsPage;
